import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash, Tag, Bank, PencilSimple, User } from "@phosphor-icons/react";
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import ExpenseFormDialog from "@/components/ExpenseFormDialog";
import ExpensesYearChart from "@/components/expenses/ExpensesYearChart";
import TzDateFixBanner from "@/components/expenses/TzDateFixBanner";

const fmt = (n, cur) => `${cur === "USD" ? "$" : "MX$"}${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const MONTHS_ES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const monthLabel = (ym) => { if(!ym) return ""; const [y,m]=ym.split("-"); return `${MONTHS_ES[parseInt(m,10)-1]} ${y.slice(2)}`; };

const COLOR_OPTS = ["#7E57C2","#FFC000","#E0000F","#1E88E5","#00873D","#FF7043","#26A69A","#52525B"];

export default function Expenses() {
  const [categories, setCategories] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [month, setMonth] = useState(todayISO().slice(0,7));
  const [filterCat, setFilterCat] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [showCatForm, setShowCatForm] = useState(false);
  const [catForm, setCatForm] = useState({ name: "", color: COLOR_OPTS[0], is_investment: false });
  const [editingCatId, setEditingCatId] = useState(null);   // id de la categoría en edición
  const [toDelExp, setToDelExp] = useState(null);
  const [toDelCat, setToDelCat] = useState(null);
  const [delReason, setDelReason] = useState("");
  const [editing, setEditing] = useState(null);
  const [fx, setFx] = useState(17);
  const [chartVer, setChartVer] = useState(0);
  const reloadAll = () => { load(); setChartVer((v) => v + 1); };

  const load = async () => {
    const [c, e] = await Promise.all([
      api.get("/expense-categories"),
      api.get("/expenses", { params: { month, category_id: filterCat } }),
    ]);
    setCategories(c.data); setExpenses(e.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, filterCat]);

  const total = useMemo(() => expenses.reduce((s,e)=>s + (e.currency==="MXN"?e.amount:e.amount*fx), 0), [expenses, fx]);
  const byCat = useMemo(() => {
    const m = {};
    expenses.forEach((e)=>{
      m[e.category_id] = m[e.category_id] || { name: e.category_name, color: e.category_color, total: 0 };
      m[e.category_id].total += e.currency==="MXN"?e.amount:e.amount*fx;
    });
    return Object.values(m).sort((a,b)=>b.total-a.total);
  }, [expenses, fx]);

  const submitCat = async (e) => {
    e.preventDefault();
    try {
      if (editingCatId) {
        await api.put(`/expense-categories/${editingCatId}`, catForm);
        toast.success("Categoría actualizada");
      } else {
        await api.post("/expense-categories", catForm);
        toast.success("Categoría creada");
      }
      setShowCatForm(false);
      setEditingCatId(null);
      setCatForm({ name: "", color: COLOR_OPTS[0], is_investment: false });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const openEditCat = (c) => {
    setEditingCatId(c.id);
    setCatForm({
      name: c.name || "",
      color: c.color || COLOR_OPTS[0],
      is_investment: !!c.is_investment,
    });
    setShowCatForm(true);
  };

  const openNewCat = () => {
    setEditingCatId(null);
    setCatForm({ name: "", color: COLOR_OPTS[0], is_investment: false });
    setShowCatForm(true);
  };

  const delExpense = async () => {
    if (!toDelExp) return;
    const reason = delReason.trim();
    if (!reason) { toast.error("La justificación es requerida"); return; }
    try {
      await api.delete(`/expenses/${toDelExp.id}`, { params: { reason } });
      toast.success("Gasto eliminado");
      reloadAll();
    }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setToDelExp(null); setDelReason(""); }
  };

  const delCat = async () => {
    if (!toDelCat) return;
    try { await api.delete(`/expense-categories/${toDelCat.id}`); toast.success("Categoría eliminada"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setToDelCat(null); }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">CONTABILIDAD</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Gastos</h1>
          <p className="text-zinc-500 mt-1 text-sm">{expenses.length} gastos en {monthLabel(month)} · Total <b className="numeric">MX${fmt(total).replace("MX$","")}</b></p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={openNewCat} variant="outline" className="gap-2" data-testid="new-cat-btn">
            <Tag size={16} weight="duotone"/> Nueva categoría
          </Button>
          <Button onClick={()=>setShowForm(true)} disabled={categories.length===0} className="bg-[#002FA7] hover:bg-[#002277] text-white gap-2" data-testid="new-expense-btn">
            <Plus size={16} weight="bold"/> Nuevo gasto
          </Button>
        </div>
      </header>

      {/* Categories chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-mono-data uppercase tracking-wider text-zinc-500">Categorías:</span>
        <button onClick={()=>setFilterCat("all")} data-testid="cat-chip-all" className={`px-3 py-1 text-xs font-medium rounded-sm border transition-all ${filterCat==="all"?"bg-[#002FA7] text-white border-transparent":"bg-white border-zinc-200 text-zinc-600"}`}>Todas</button>
        {categories.map((c)=>(
          <div key={c.id} className="inline-flex items-center gap-0.5 group">
            <button onClick={()=>setFilterCat(c.id)}
              className={`px-3 py-1 text-xs font-medium rounded-sm border transition-all ${filterCat===c.id?"border-transparent text-white":"bg-white border-zinc-200 text-zinc-600"}`}
              style={filterCat===c.id?{background:c.color}:{}} data-testid={`cat-chip-${c.id}`}
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full mr-1" style={{background:c.color}}/>{c.name}
              {c.is_investment && <span className="ml-1 text-[9px] font-bold" title="Categoría marcada como inversión">▲</span>}
            </button>
            <button
              onClick={()=>openEditCat(c)}
              className="text-zinc-300 hover:text-[#002FA7] text-[10px] px-1 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Editar categoría"
              data-testid={`cat-edit-${c.id}`}
            >✎</button>
            <button onClick={()=>setToDelCat(c)} className="text-zinc-300 hover:text-[#E0000F] text-xs opacity-0 group-hover:opacity-100 transition-opacity" title="Eliminar categoría">×</button>
          </div>
        ))}
        <div className="ml-auto">
          <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="w-44 h-8 text-xs" data-testid="month-input"/>
        </div>
      </div>

      {/* Fechas corridas por zona horaria (solo admin, aparece si hay registros afectados) */}
      <TzDateFixBanner onFixed={reloadAll} />

      {/* Gastos del año (ene→dic) */}
      <ExpensesYearChart categoryId={filterCat} refreshKey={chartVer} onFx={setFx} />

      {/* Monthly summary chart */}
      {byCat.length > 0 && (
        <section className="border border-zinc-200 bg-white p-6">
          <h3 className="font-heading text-lg font-semibold mb-4">Gastos por categoría · {monthLabel(month)}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={byCat} layout="vertical" margin={{left: 20}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5"/>
              <XAxis type="number" tick={{fontSize:11}} tickFormatter={(v)=>v >= 1000 ? `${(v/1000).toFixed(0)}K` : v}/>
              <YAxis type="category" dataKey="name" tick={{fontSize:11}} width={120}/>
              <Tooltip formatter={(v)=>`MX$${fmt(v).replace("MX$","")}`}/>
              <Bar dataKey="total" radius={[0,4,4,0]}>
                {byCat.map((c)=><Cell key={c.name} fill={c.color}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </section>
      )}

      {/* Expense list */}
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="expenses-table">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Fecha</th>
              <th className="text-left px-4 py-3">Título</th>
              <th className="text-left px-4 py-3">Categoría</th>
              <th className="text-left px-4 py-3">Creado por</th>
              <th className="text-left px-4 py-3">Proveedor</th>
              <th className="text-left px-4 py-3">Banco</th>
              <th className="text-right px-4 py-3">Monto</th>
              <th className="text-left px-4 py-3">Nota</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {expenses.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-16 text-center text-zinc-400 dotted-bg">Sin gastos en este periodo</td></tr>
            )}
            {expenses.map((e) => (
              <tr key={e.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                <td className="px-4 py-3 text-zinc-600 font-mono-data text-xs">{e.date}</td>
                <td className="px-4 py-3 font-semibold text-zinc-800 max-w-[220px] truncate" title={e.title}>
                  {e.title || <span className="text-zinc-400 font-normal italic">sin título</span>}
                  {Array.isArray(e.edit_history) && e.edit_history.length > 0 && (
                    <span className="ml-2 text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" title={`Editado ${e.edit_history.length} vez(ces). Última razón: ${e.edit_history[e.edit_history.length - 1]?.reason || ''}`}>editado</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-sm" style={{background:e.category_color+"20", color:e.category_color}}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{background:e.category_color}}/> {e.category_name}
                  </span>
                </td>
                <td className="px-4 py-3 text-zinc-700 text-xs">
                  <span className="inline-flex items-center gap-1" title={e.user_email || "—"}>
                    <User size={11} className="text-zinc-400"/>
                    {e.user_name || e.user_email || "—"}
                  </span>
                </td>
                <td className="px-4 py-3 text-zinc-700">{e.vendor || "—"}</td>
                <td className="px-4 py-3 text-zinc-700 text-xs">
                  {e.bank_name ? <span className="inline-flex items-center gap-1"><Bank size={12}/>{e.bank_name}</span> : "—"}
                </td>
                <td className="px-4 py-3 text-right numeric font-semibold text-[#E0000F]">−{fmt(e.amount, e.currency)}</td>
                <td className="px-4 py-3 text-zinc-500 text-xs max-w-[240px] truncate">{e.note || "—"}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <button onClick={()=>setEditing(e)} className="p-2 hover:bg-blue-100 text-[#002FA7] rounded" data-testid={`edit-exp-${e.id}`} title="Editar">
                    <PencilSimple size={14}/>
                  </button>
                  <button onClick={()=>setToDelExp(e)} className="p-2 hover:bg-red-100 text-[#E0000F] rounded ml-1" data-testid={`del-exp-${e.id}`} title="Eliminar">
                    <Trash size={14}/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* New expense */}
      <ExpenseFormDialog open={showForm} onOpenChange={setShowForm} onSaved={reloadAll} />

      {/* Edit expense (any user's) */}
      <ExpenseFormDialog
        open={!!editing}
        onOpenChange={(o) => { if (!o) setEditing(null); }}
        onSaved={reloadAll}
        existing={editing}
      />

      {/* New/Edit category */}
      <Dialog open={showCatForm} onOpenChange={(o) => { setShowCatForm(o); if (!o) { setEditingCatId(null); setCatForm({ name: "", color: COLOR_OPTS[0], is_investment: false }); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editingCatId ? "Editar categoría" : "Nueva categoría"}</DialogTitle></DialogHeader>
          <form onSubmit={submitCat} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Nombre *</Label>
              <Input value={catForm.name} onChange={(e)=>setCatForm({...catForm,name:e.target.value})} required placeholder="Renta, Servicios, Sueldos..." className="mt-1" data-testid="cat-name"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Color</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {COLOR_OPTS.map((c)=>(
                  <button type="button" key={c} onClick={()=>setCatForm({...catForm,color:c})}
                    className={`w-7 h-7 rounded-sm border-2 ${catForm.color===c?"border-zinc-900":"border-transparent"}`}
                    style={{background:c}}/>
                ))}
              </div>
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer border border-zinc-200 rounded p-3 bg-zinc-50/50 hover:bg-zinc-50" data-testid="cat-investment-label">
              <input
                type="checkbox"
                checked={!!catForm.is_investment}
                onChange={(e) => setCatForm({ ...catForm, is_investment: e.target.checked })}
                className="w-4 h-4 mt-0.5 accent-[#7E57C2]"
                data-testid="cat-is-investment"
              />
              <div className="text-xs">
                <div className="font-semibold text-zinc-800">Incluir en el métrico de inversiones</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  Los gastos de esta categoría se suman en el bloque de <b>Inversiones</b> del Dashboard Financiero y se restan del cálculo de gastos operativos. Ideal para compra de lotes, refacciones, mercancía para reventa, etc.
                </div>
              </div>
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setShowCatForm(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="cat-submit">{editingCatId ? "Guardar cambios" : "Crear"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelExp} onOpenChange={(o)=>{ if (!o) { setToDelExp(null); setDelReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar gasto?</AlertDialogTitle>
            <AlertDialogDescription>{toDelExp && <>Se eliminará el gasto de <b>{fmt(toDelExp.amount, toDelExp.currency)}</b>{toDelExp.bank_name ? <> y se regresará el dinero al banco <b>{toDelExp.bank_name}</b></> : ""}. Se guardará en el historial de auditoría.</>}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="border border-amber-300 bg-amber-50 rounded p-3 my-2">
            <Label className="text-xs uppercase tracking-wider text-amber-900">Justificación * <span className="font-normal normal-case">(¿por qué eliminas este gasto?)</span></Label>
            <Input
              autoFocus
              value={delReason}
              onChange={(e) => setDelReason(e.target.value)}
              placeholder="Ej. Se registró por equivocación..."
              className="mt-1 bg-white"
              data-testid="exp-delete-reason"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={delExpense} disabled={!delReason.trim()} className="bg-[#E0000F] hover:bg-[#b8000c] text-white" data-testid="exp-delete-confirm">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toDelCat} onOpenChange={(o)=>{ if (!o) setToDelCat(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar categoría?</AlertDialogTitle>
            <AlertDialogDescription>Solo se puede eliminar si no tiene gastos asociados.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={delCat} className="bg-[#E0000F] hover:bg-[#b8000c] text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
